import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  buildRecoveryPath,
  captureRecoveryReturnTo,
  getRecoveryByClassSlug,
  isOnRecoverySlug,
  readRecoveryQueryContext,
  RecoveryLayout,
  RecoverySurface,
} from '../../recovery/index.js';
import { TenantProvider } from '../../tenant/TenantProvider.js';
import { Card } from '../../ui/Card.js';

/**
 * L1 unknown-error fallback (MS-REC-076 Option A).
 * Full reload via `window.location.assign` — clears EB state; RecoveryHost mounts clean.
 * Must not emit `error_shown` (RecoveryHost owns analytics).
 *
 * JourneyProvider sits below L1 — do NOT call useJourneyContext here.
 * Pass journey only from a known URL search param when present (buildRecoveryPath supports it).
 *
 * When already on `…/recovery/unknown-error`, do not re-assign (avoids nested returnTo loops).
 * Render MS-REC-076 RecoverySurface inline (TenantProvider is inside L1, so Host is unmounted)
 * and replace-normalize nested returnTo like RecoveryHost (G3).
 *
 * Public i18n / recovery surface only — never renders Error.message, stack, or componentStack.
 */
export function CustomerUnknownErrorFallback(): JSX.Element {
  const { t } = useTranslation('customer');
  const { tenantCode: tenantCodeParam } = useParams<{ tenantCode: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const navigatedRef = useRef(false);

  const tenantFromPath = location.pathname.split('/').filter(Boolean)[0];
  const tenantCode =
    (tenantCodeParam !== undefined && tenantCodeParam.trim().length > 0
      ? tenantCodeParam.trim()
      : undefined) ??
    (tenantFromPath !== undefined && tenantFromPath.length > 0 ? tenantFromPath : undefined);

  const alreadyOnUnknownError = isOnRecoverySlug(location.pathname, 'unknown-error');

  useEffect(() => {
    if (navigatedRef.current) {
      return;
    }
    if (tenantCode === undefined) {
      return;
    }

    if (alreadyOnUnknownError) {
      navigatedRef.current = true;
      // Mirror RecoveryHost mount normalize — peel nested recovery returnTo in the address bar.
      const params = new URLSearchParams(
        location.search.startsWith('?') ? location.search.slice(1) : location.search,
      );
      const rawReturnTo = params.get('returnTo');
      if (rawReturnTo === null || rawReturnTo.length === 0) {
        return;
      }
      const peeledContext = readRecoveryQueryContext(location.search);
      if (rawReturnTo === peeledContext.returnTo) {
        return;
      }
      navigate(buildRecoveryPath(tenantCode, 'unknown-error', peeledContext), {
        replace: true,
      });
      return;
    }

    navigatedRef.current = true;
    const journeyParam = new URLSearchParams(location.search).get('journey');
    const journey =
      journeyParam !== null && journeyParam.length > 0 ? journeyParam : undefined;
    const returnTo = captureRecoveryReturnTo(location.pathname, location.search);
    window.location.assign(
      buildRecoveryPath(tenantCode, 'unknown-error', {
        ...(returnTo !== undefined ? { returnTo } : {}),
        ...(journey !== undefined ? { journey } : {}),
      }),
    );
  }, [
    alreadyOnUnknownError,
    tenantCode,
    location.pathname,
    location.search,
    navigate,
  ]);

  if (alreadyOnUnknownError && tenantCode !== undefined) {
    const definition = getRecoveryByClassSlug('unknown-error');
    if (definition !== undefined) {
      const context = readRecoveryQueryContext(location.search);
      return (
        <TenantProvider tenantCode={tenantCode}>
          <RecoveryLayout title={t(`${definition.i18nKeyPrefix}.title`)}>
            <RecoverySurface definition={definition} context={context} />
          </RecoveryLayout>
        </TenantProvider>
      );
    }
  }

  return (
    <div
      role="alert"
      aria-live="assertive"
      data-testid="customer-eb-l1-fallback"
      className="mx-auto flex min-h-[50vh] w-full min-w-0 max-w-lg flex-col justify-center p-6"
    >
      <Card padded className="w-full min-w-0">
        <h2 className="text-lg font-semibold text-[var(--color-on-surface)]">
          {t('app.errorBoundary.title')}
        </h2>
        <p className="mt-2 text-sm text-[var(--color-on-surface-muted)]">
          {t('app.errorBoundary.message')}
        </p>
      </Card>
    </div>
  );
}
